import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
	activityAPI,
	type ActivityCategory,
	type ActivityLogEntry,
	type ActivityLogPage,
} from "../../services/api";

export const RECENT_ACTIVITY_LIMIT = 20;
export const ACTIVITY_PAGE_SIZE = 20;

interface ActivityState {
	// The dashboard card and the Activity page each keep their own list, so
	// the card's polling never replaces the page the user is reading.
	recent: {
		entries: ActivityLogEntry[];
		loading: boolean;
		error: string | null;
		latestRequestId: string | null;
	};
	logs: ActivityLogEntry[];
	total: number;
	page: number;
	limit: number;
	// null lists every category
	category: ActivityCategory | null;
	loading: boolean;
	error: string | null;
	// Id of the most recent page request, so a slower earlier one - say, the
	// page the user clicked away from - cannot overwrite the one now on screen.
	latestFetchRequestId: string | null;
}

const initialState: ActivityState = {
	recent: {
		entries: [],
		loading: false,
		error: null,
		latestRequestId: null,
	},
	logs: [],
	total: 0,
	page: 1,
	limit: ACTIVITY_PAGE_SIZE,
	category: null,
	loading: false,
	error: null,
	latestFetchRequestId: null,
};

export const fetchRecentActivity = createAsyncThunk<
	ActivityLogPage,
	void,
	{ rejectValue: string }
>("activity/fetchRecentActivity", async (_params, { rejectWithValue }) => {
	try {
		const response = await activityAPI.getAll({
			page: 1,
			limit: RECENT_ACTIVITY_LIMIT,
		});
		return response.data.data;
	} catch (error: unknown) {
		if (error && typeof error === "object" && "response" in error) {
			const axiosError = error as {
				response?: { status?: number; data?: { message?: string } };
			};
			// Let the interceptor refresh the session and retry
			if (axiosError.response?.status === 401) throw error;
			return rejectWithValue(axiosError.response?.data?.message || "Failed to load recent activity");
		}
		return rejectWithValue("Failed to load recent activity");
	}
});

export const fetchActivityPage = createAsyncThunk<
	ActivityLogPage,
	{ page: number; category: ActivityCategory | null },
	{ rejectValue: string }
>("activity/fetchActivityPage", async ({ page, category }, { rejectWithValue }) => {
	try {
		const response = await activityAPI.getAll({
			page,
			limit: ACTIVITY_PAGE_SIZE,
			category: category ?? undefined,
		});
		return response.data.data;
	} catch (error: unknown) {
		if (error && typeof error === "object" && "response" in error) {
			const axiosError = error as {
				response?: { status?: number; data?: { message?: string } };
			};
			// Let the interceptor refresh the session and retry
			if (axiosError.response?.status === 401) throw error;
			return rejectWithValue(axiosError.response?.data?.message || "Failed to load activity");
		}
		return rejectWithValue("Failed to load activity");
	}
});

const activitySlice = createSlice({
	name: "activity",
	initialState,
	reducers: {
		setPage: (state, action: PayloadAction<number>) => {
			state.page = action.payload;
		},
		// A new category is a different result set, so the old page number
		// could point past its end
		setCategory: (state, action: PayloadAction<ActivityCategory | null>) => {
			state.category = action.payload;
			state.page = 1;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchRecentActivity.pending, (state, action) => {
				state.recent.loading = true;
				state.recent.error = null;
				state.recent.latestRequestId = action.meta.requestId;
			})
			.addCase(fetchRecentActivity.fulfilled, (state, action) => {
				if (state.recent.latestRequestId !== action.meta.requestId) return;
				state.recent.loading = false;
				state.recent.entries = action.payload.logs;
			})
			.addCase(fetchRecentActivity.rejected, (state, action) => {
				if (state.recent.latestRequestId !== action.meta.requestId) return;
				state.recent.loading = false;
				state.recent.error = action.payload ?? "Failed to load recent activity";
				// Left in place, the old entries would pass for current ones
				state.recent.entries = [];
			})
			.addCase(fetchActivityPage.pending, (state, action) => {
				state.loading = true;
				state.error = null;
				state.latestFetchRequestId = action.meta.requestId;
			})
			.addCase(fetchActivityPage.fulfilled, (state, action) => {
				if (state.latestFetchRequestId !== action.meta.requestId) return;
				state.loading = false;
				state.logs = action.payload.logs;
				state.total = action.payload.total;
			})
			.addCase(fetchActivityPage.rejected, (state, action) => {
				if (state.latestFetchRequestId !== action.meta.requestId) return;
				state.loading = false;
				state.error = action.payload ?? "Failed to load activity";
				// `total` stays, so the pagination needed to get back off a
				// failing page is still there
				state.logs = [];
			});
	},
});

export const { setPage, setCategory } = activitySlice.actions;
export default activitySlice.reducer;
